import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTrash, FaEdit, FaBox } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  fetchFeatures,
  deleteFeatures,
  clearState,
} from "../../redux/Slices/FeaturesSlice";
import { useTitle } from "../../context/TitleContext";
import PermissionMotionButton from "../PermissionMotionButton";
import PermissionButton from "../PermissionButton";
import Pagination from '../../components/Pagination';

// BUG-56 fix: عرض أيقونة مناسبة لمميزات المتجر (بدلاً من صورة شخص افتراضية) عند عدم وجود لوجو مرفوع
const FeatureLogo = ({ logo }) => {
  const [imgError, setImgError] = useState(false);

  if (!logo || imgError) {
    return (
      <FaBox size={20} style={{ color: "#94a3b8" }} />
    );
  }

  return (
    <img
      src={logo}
      alt="logo"
      onError={() => setImgError(true)}
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
  );
};

const Features = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingFeature, setDeletingFeature] = useState(null);

  const { features, meta, isLoading: loading, error, success } = useSelector(
    (state) => state.features
  );

  useEffect(() => {
    setTitle("المميزات");
  }, [setTitle]);

  useEffect(() => {
    dispatch(clearState());
    dispatch(fetchFeatures(currentPage));
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (success) {
      setShowDeleteModal(false);
      setDeletingFeature(null);
      setTimeout(() => {
        toast.success("تم الحذف بنجاح", { position: "top-center", zIndex: 9999 });
      }, 100);
      dispatch(fetchFeatures(currentPage));
      dispatch(clearState());
    }
    if (error) {
      toast.error("لم يتم الحذف", { position: "top-center", zIndex: 9999 });
      dispatch(clearState());
    }
  }, [success, error, dispatch, currentPage]);

  const handlePageChange = (page) => {
    if (loading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };

  const handleDelete = (feature) => {
    setDeletingFeature(feature);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingFeature) {
      dispatch(deleteFeatures(deletingFeature.id));
      setShowDeleteModal(false);
      setDeletingFeature(null);
    }
  };



  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      
      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            📂 المميزات
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>إدارة وتعديل مميزات المتجر المعروضة</p>
        </div>
        <PermissionMotionButton
          permission="create-features"
          onClick={() => navigate("/add-feature")}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          <FaPlus size={12} /> إضافة ميزة جديدة
        </PermissionMotionButton>
      </div>

      {/* ── Loading ───────────────────────────────────── */}
      {loading && !features?.data?.length && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ height: 78, borderRadius: 12, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </div>
      )}

      {/* ── Empty ─────────────────────────────────────── */}
      {!loading && features?.data?.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 52 }}>📂</div>
          <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد مميزات مسجلة</p>
        </div>
      )}

      {/* ── Features List ──────────────────────────────── */}
      {!loading && features?.data?.length > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {features.data.map((feature, idx) => (
              <div key={feature.id}
                style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 1px 4px rgba(0,0,0,.04)", transition: "box-shadow .2s" }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,.1)")}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.04)")}
              >
                {/* Feature logo image */}
                <div style={{ width: 48, height: 48, borderRadius: 12, border: "1.5px solid #e2e8f0", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", flexShrink: 0 }}>
                  <FeatureLogo logo={feature.logo} />
                </div>

                {/* content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 3 }}>
                    <strong style={{ fontSize: 15, color: "#0f172a" }}>{feature.title_ar}</strong>
                    {feature.status === "active" ? (
                      <span style={{ padding: "2px 10px", borderRadius: 20, background: "#d1fae5", color: "#065f46", fontSize: 11, fontWeight: 700 }}>مفعل</span>
                    ) : (
                      <span style={{ padding: "2px 10px", borderRadius: 20, background: "#f1f5f9", color: "#64748b", fontSize: 11, fontWeight: 700 }}>غير مفعل</span>
                    )}
                  </div>
                  {feature.title_en && (
                    <div style={{ fontSize: 12, color: "#94a3b8", direction: "ltr", textAlign: "right" }}>
                      {feature.title_en}
                    </div>
                  )}
                </div>

                {/* index badge */}
                <div style={{ flexShrink: 0, padding: "0 12px", color: "#cbd5e1", fontSize: 13, fontWeight: 700 }}>
                  #{meta ? (meta.current_page - 1) * (meta.per_page || 10) + idx + 1 : idx + 1}
                </div>

                {/* actions */}
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <PermissionButton permission="edit-features" title="تعديل"
                    onClick={() => {
                      sessionStorage.setItem(`feature_${feature.id}`, JSON.stringify({ id: feature.id, title_ar: feature.title_ar, title_en: feature.title_en }));
                      navigate(`/edit-feature/${feature.id}`);
                    }}
                    style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                    <FaEdit size={14} />
                  </PermissionButton>
                  <PermissionButton permission="delete-features" onClick={() => handleDelete(feature)} disabled={loading} title="حذف"
                    style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                    <FaTrash size={14} />
                  </PermissionButton>
                </div>
              </div>
            ))}
          </div>
          <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
        </>
      )}

      {/* ── Delete Modal ──────────────────────────────── */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingFeature(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف هذه الميزة؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                {deletingFeature?.title_ar}
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingFeature(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} disabled={loading} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {loading ? <><span className="spinner-border spinner-border-sm"></span> جاري الحذف...</> : "حذف الميزة"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Features;
